import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortObjectOptions } from '@/utils/sortOptions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '../utils/toast';
import { Link as LinkIcon, Unlink, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TagToTopicsPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [trainingTopics, setTrainingTopics] = useState([]);
  const [questionnaires, setQuestionnaires] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [formData, setFormData] = useState({
    questionnaire_id: '',
    is_mandatory: true,
    passing_required_for_completion: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch training chapters
      const topicsRes = await axios.get(`${API}/training/content`, { headers });
      setTrainingTopics(topicsRes.data || []);

      // Fetch questionnaires (only published)
      const questionnairesRes = await axios.get(`${API}/assessments/questionnaires`, { headers });
      const published = (questionnairesRes.data.questionnaires || []).filter(q => q.status === 'published');
      setQuestionnaires(published);

      // Fetch existing mappings
      const mappingsRes = await axios.get(`${API}/assessments/topic-mappings`, { headers });
      setMappings(mappingsRes.data.mappings || []);
    } catch (error) {
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  const handleTagAssessment = async () => {
    if (!formData.questionnaire_id) {
      toast.error('Please select a questionnaire');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/assessments/tag-to-topic`, {
        training_topic_id: selectedTopic.id,
        questionnaire_id: formData.questionnaire_id,
        is_mandatory: formData.is_mandatory,
        passing_required_for_completion: formData.passing_required_for_completion
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Assessment tagged successfully!');
      setShowTagModal(false);
      setFormData({
        questionnaire_id: '',
        is_mandatory: true,
        passing_required_for_completion: true
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to tag assessment');
    }
  };

  const handleUntagAssessment = async (topicId) => {
    if (!window.confirm('Are you sure you want to remove this assessment from the chapter?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/assessments/topic/${topicId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Assessment untagged successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to untag assessment');
    }
  };

  const openTagModal = (topic) => {
    setSelectedTopic(topic);
    
    // Check if topic already has mapping
    const existing = mappings.find(m => m.training_topic_id === topic.id);
    if (existing) {
      setFormData({
        questionnaire_id: existing.questionnaire_id,
        is_mandatory: existing.is_mandatory,
        passing_required_for_completion: existing.passing_required_for_completion
      });
    } else {
      setFormData({
        questionnaire_id: '',
        is_mandatory: true,
        passing_required_for_completion: true
      });
    }
    
    setShowTagModal(true);
  };

  const getTopicMapping = (topicId) => {
    return mappings.find(m => m.training_topic_id === topicId);
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex items-center mb-6">
          <Button variant="outline" onClick={() => navigate('/assessments')} className="mr-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tag Assessments to Chapters</h1>
            <p className="text-gray-600 mt-1">Link assessments to training chapters for learner completion tracking</p>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Training Chapters</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{trainingTopics.length}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <LinkIcon className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Tagged Chapters</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{mappings.length}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Untagged Chapters</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{trainingTopics.length - mappings.length}</p>
              </div>
              <div className="bg-gray-100 p-3 rounded-lg">
                <XCircle className="w-4 h-4 text-gray-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Chapters List */}
        {loading ? (
          <div className="text-center py-12">Loading chapters...</div>
        ) : trainingTopics.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-gray-500 mb-4">No training chapters available.</p>
            <Button onClick={() => navigate('/training')}>Go to Training Chapters</Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {trainingTopics.map((topic) => {
              const mapping = getTopicMapping(topic.id);
              const questionnaire = mapping ? questionnaires.find(q => q.questionnaire_id === mapping.questionnaire_id) : null;

              return (
                <Card key={topic.id} className={`p-6 transition-all ${mapping ? 'border-green-500' : 'border-gray-200'}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-[18px] font-bold text-gray-900">{topic.title}</h3>
                        {mapping ? (
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Tagged
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600">
                            <XCircle className="w-4 h-4 mr-1" />
                            Not Tagged
                          </Badge>
                        )}
                      </div>

                      {mapping && questionnaire && (
                        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 mb-1">
                                📝 {questionnaire.title}
                              </p>
                              <div className="flex flex-wrap gap-3 text-xs text-gray-600 mt-2">
                                <span>📊 {questionnaire.total_questions} questions</span>
                                <span>🎯 {questionnaire.total_points} points</span>
                                <span>✅ {questionnaire.pass_score_percentage}% pass score</span>
                              </div>
                              <div className="flex gap-4 mt-3">
                                {mapping.is_mandatory && (
                                  <Badge className="bg-orange-100 text-orange-800 text-xs">Mandatory</Badge>
                                )}
                                {mapping.passing_required_for_completion && (
                                  <Badge className="bg-red-100 text-red-800 text-xs">Passing Required</Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex space-x-2 ml-4">
                      <Button
                        onClick={() => openTagModal(topic)}
                        variant={mapping ? "outline" : "default"}
                        className={mapping ? "" : "bg-green-600 hover:bg-green-700"}
                      >
                        <LinkIcon className="w-4 h-4 mr-2" />
                        {mapping ? 'Edit' : 'Tag Assessment'}
                      </Button>
                      {mapping && (
                        <Button
                          onClick={() => handleUntagAssessment(topic.id)}
                          variant="outline"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Unlink className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Tag Assessment Modal */}
        <Dialog open={showTagModal} onOpenChange={setShowTagModal}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {getTopicMapping(selectedTopic?.id) ? 'Edit' : 'Tag'} Assessment to Chapter
              </DialogTitle>
            </DialogHeader>

            {selectedTopic && (
              <div className="space-y-6">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm font-medium text-blue-900">Training Chapter:</p>
                  <p className="text-lg font-bold text-blue-900 mt-1">{selectedTopic.title}</p>
                </div>

                <div>
                  <Label>Select Questionnaire *</Label>
                  <Select value={formData.questionnaire_id} onValueChange={(val) => setFormData({ ...formData, questionnaire_id: val })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choose a published questionnaire" />
                    </SelectTrigger>
                    <SelectContent>
                      {questionnaires.length === 0 ? (
                        <SelectItem value="none" disabled>No published questionnaires available</SelectItem>
                      ) : (
                        sortObjectOptions(questionnaires, 'title').map((q) => (
                          <SelectItem key={q.questionnaire_id} value={q.questionnaire_id}>
                            {q.title} ({q.total_questions} questions, {q.pass_score_percentage}% pass)
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="is_mandatory"
                      checked={formData.is_mandatory}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_mandatory: checked })}
                    />
                    <Label htmlFor="is_mandatory" className="cursor-pointer">
                      <span className="font-medium">Mandatory Assessment</span>
                      <p className="text-xs text-gray-600">Learners must complete this assessment</p>
                    </Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="passing_required"
                      checked={formData.passing_required_for_completion}
                      onCheckedChange={(checked) => setFormData({ ...formData, passing_required_for_completion: checked })}
                    />
                    <Label htmlFor="passing_required" className="cursor-pointer">
                      <span className="font-medium">Passing Required for Chapter Completion</span>
                      <p className="text-xs text-gray-600">Learners must pass to mark chapter as complete</p>
                    </Label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <Button variant="outline" onClick={() => setShowTagModal(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleTagAssessment} className="bg-green-600 hover:bg-green-700">
                    <LinkIcon className="w-4 h-4 mr-2" />
                    {getTopicMapping(selectedTopic?.id) ? 'Update' : 'Tag'} Assessment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default TagToTopicsPage;
